import React from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { FormAccordion, FormAccordionItem } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { FormTitle } from 'src/financial/financial-data/financial-item/forms/common/FormTitle';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  FEE,
  MortgageFeeFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/MortgageFeeFormAccordionItemList';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';

const MORTGAGE_PRODUCT = 'Mortgage';

export function Mortgage(props: ProductFormComponentProps) {
  const { onSave } = props;

  const { isCreateMode, uiData, initialValues, onDeleteConfirm, feedInfo, onConnectAsset } =
    getFinancialFormSetupValues(MORTGAGE_PRODUCT, props, [{ product: FEE, field: 'fees' }]);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
  });

  const handleSave = async (values: FormValuesType) => {
    if (FEE in values && Array.isArray(values[FEE])) {
      const mappedValues = {
        ...values[MORTGAGE_PRODUCT],
        fees: values[FEE].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[MORTGAGE_PRODUCT]);
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete={'off'}>
        <FormContent form={form} feedInfo={feedInfo} onConnect={onConnectAsset} />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({ form, feedInfo, onConnect }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  return (
    <FinancialFormContainer
      title={<FormTitle title={'Liabilities > Mortgage'} />}
      feedInfo={feedInfo}
      onConnect={onConnect}
    >
      <FormAccordion defaultValue={['summary', 'total', 'breakdown', 'protected', 'fee']}>
        <FormAccordionItem value="summary" label="Summary">
          <Grid>
            <GridPair
              first={<MortgageField field="type" />}
              second={<MortgageField field="description" />}
            />
            <GridPair
              first={<MortgageField field="provider" />}
              second={<MortgageField field="provider_reference_number" />}
            />
            <GridPair
              first={<MortgageField field="status" />}
              second={<MortgageField field="is_joint" />}
            />
            <GridPair
              isHighlighted
              first={<MortgageField field="remaining_balance" />}
              second={<MortgageField field="remaining_balance_date" />}
            />
          </Grid>
        </FormAccordionItem>

        <FormAccordionItem value="total" label="Repayment information">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="repayment_amount" />}
              second={<MortgageField field="repayment_frequency" />}
            />
            <GridPair first={<MortgageField field="repayment_type" />} />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Interest rate details">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="interest_rate_type" />}
              second={<MortgageField field="interest_rate" />}
            />
            <GridPair
              isHighlighted
              first={<MortgageField field="rate_period_start" />}
              second={<MortgageField field="rate_period_end" />}
            />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Initial loan amount and term">
          <Grid>
            <GridPair
              isHighlighted
              first={<MortgageField field="initial_amount" />}
              second={<MortgageField field="term_start_date" />}
            />
            <GridPair first={<MortgageField field="term_end_date" />} />
          </Grid>
        </FormAccordionItem>
        <FormAccordionItem value="total" label="Charges">
          <Grid>
            <GridPair
              first={<MortgageField field="early_payment_charge" />}
              second={<MortgageField field="redemption_fee_amount" />}
            />
          </Grid>
        </FormAccordionItem>
        <MortgageFeeFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};

type DefinedBenefitPensionFieldProps = {
  field: string;
};

const MortgageField = ({ field }: DefinedBenefitPensionFieldProps) => (
  <FinancialFormField field={field} product={MORTGAGE_PRODUCT} />
);
